/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../Reducer/loaderSlice";
import Handler from "../Helpers/Handler";
import { Row, Col, Card, CardBody, Button, Badge, Spinner } from "reactstrap";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend } from 'recharts';
import dayjs from "dayjs";

// ⭐ MUI Imports machan
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';

const AnalyticsMasterDashboard = () => {
    const dispatch = useDispatch();
    const loading = useSelector(s => s.loading.loading);
    const [stats, setStats] = useState(null);

    // ⭐ MUI Filter States
    const [filters, setFilters] = useState({
        from_date: dayjs().startOf("month"),
        to_date: dayjs()
    });

    const loadMasterStats = async () => {
        // Validation: To date kandaipa From date-ku apram dhaan irukanum machan
        if (filters.to_date.isBefore(filters.from_date)) {
            alert("Machan, 'To' date must be after 'From' date!");
            return;
        }

        dispatch(loadingTrue());
        const url = `/reports/dashboard.php?from_date=${filters.from_date.format("YYYY-MM-DD")}&to_date=${filters.to_date.format("YYYY-MM-DD")}`;
        const res = await Handler({ method: "get", url });
        
        if (res?.success || res?.status) {
            setStats(res.data);
        }
        dispatch(loadingFalse());
    };

    useEffect(() => { loadMasterStats(); }, []);

    if (!stats) return (
        <div className="d-flex justify-content-center align-items-center min-vh-100 bg-[#f8fafc]">
            <Spinner color="primary" /> <span className="ms-3 fw-800 text-slate-600">Syncing Intelligence...</span>
        </div>
    );

    const { summary, charts } = stats;

    // Data format conversion for Recharts
    const trendData = (charts?.revenue_trend || []).map((rev) => {
        const exp = charts?.expense_trend?.find(e => e.date === rev.date);
        return {
            date: dayjs(rev.date).format("DD MMM"),
            income: Number(parseFloat(rev.total || 0).toFixed(2)),
            costs: Number(parseFloat(exp?.total || 0).toFixed(2))
        };
    });

    const expensePie = [
        { name: 'Driver Salary', value: Number(parseFloat(summary?.driver_salary || 0)) },
        { name: 'Operations', value: Number(parseFloat(summary?.other_expenses || 0)) }
    ];

    const COLORS = ['#4318FF', '#EE5D50', '#00D1FF', '#FFB547', '#10B981'];

    return (
        <div className="p-4 min-vh-100" style={{ backgroundColor: "#F4F7FE", fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            
            {/* Header & MUI Advanced Filter Bar */}
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center mb-5 gap-3">
                <div>
                    <h2 className="fw-800 text-dark mb-1 tracking-tight">Executive Command Center</h2>
                    <p className="text-muted fw-500 mb-0">Holistic Oversight of Logistics & Finances</p>
                </div>

                <div className="bg-white p-3 rounded-pill shadow-sm border d-flex align-items-center gap-3 px-4">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className="d-flex align-items-center gap-2">
                            <small className="text-muted fw-bold">FROM</small>
                            <DatePicker
                                value={filters.from_date}
                                onChange={(val) => setFilters({ ...filters, from_date: val })}
                                slotProps={{ textField: { size: 'small', variant: 'standard', InputProps: { disableUnderline: true }, sx: { width: 130 } } }}
                            />
                        </div>
                        <div className="vr" style={{ height: '20px' }}></div>
                        <div className="d-flex align-items-center gap-2">
                            <small className="text-muted fw-bold">TO</small>
                            <DatePicker
                                value={filters.to_date}
                                onChange={(val) => setFilters({ ...filters, to_date: val })}
                                minDate={filters.from_date} // ⭐ Built-in MUI Validation
                                slotProps={{ textField: { size: 'small', variant: 'standard', InputProps: { disableUnderline: true }, sx: { width: 130 } } }}
                            />
                        </div>
                    </LocalizationProvider>
                    <Button color="primary" className="rounded-pill px-4 fw-bold shadow-indigo" onClick={loadMasterStats} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : "REFRESH"}
                    </Button>
                </div>
            </div>

            {/* Row 1: KPI Cards */}
            <Row className="mb-5 g-4">
                {[
                    { label: "Cash Inflow", val: summary?.total_revenue, theme: "linear-gradient(135deg, #4318FF 0%, #5E35B1 100%)", icon: "wallet2" },
                    { label: "Net Earnings", val: summary?.net_profit, theme: "linear-gradient(135deg, #10B981 0%, #059669 100%)", icon: "graph-up-arrow" },
                    { label: "Total Fleet", val: summary?.total_trips, theme: "linear-gradient(135deg, #FFB547 0%, #F57C00 100%)", icon: "truck" },
                    { label: "Client Growth", val: summary?.new_customers, theme: "linear-gradient(135deg, #EE5D50 0%, #C62828 100%)", icon: "people" }
                ].map((kpi, idx) => (
                    <Col key={idx} lg={3} md={6}>
                        <div className="p-4 rounded-5 shadow-lg text-white h-100 d-flex justify-content-between align-items-center transition-up" style={{ background: kpi.theme }}>
                            <div>
                                <span className="opacity-80 small fw-bold text-uppercase ls-1">{kpi.label}</span>
                                <h2 className="fw-800 mb-0 mt-2">
                                    {kpi.label.includes("Cash") || kpi.label.includes("Net") ? `$${parseFloat(kpi.val || 0).toLocaleString()}` : kpi.val}
                                </h2>
                            </div>
                            <i className={`bi bi-${kpi.icon} display-5 opacity-30`}></i>
                        </div>
                    </Col>
                ))}
            </Row>

            {/* Row 2: Charts Area */}
            <Row className="g-4 mb-5">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-5 p-3">
                        <CardBody>
                            <h5 className="fw-800 text-dark mb-4">Financial Trajectory</h5>
                            <div style={{ width: '100%', height: 380 }}>
                                <ResponsiveContainer>
                                    <AreaChart data={trendData}>
                                        <defs>
                                            <linearGradient id="colorInc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4318FF" stopOpacity={0.1}/><stop offset="95%" stopColor="#4318FF" stopOpacity={0}/></linearGradient>
                                            <linearGradient id="colorExp" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#EE5D50" stopOpacity={0.1}/><stop offset="95%" stopColor="#EE5D50" stopOpacity={0}/></linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={11} />
                                        <YAxis axisLine={false} tickLine={false} fontSize={11} />
                                        <Tooltip contentStyle={{ borderRadius: '15px', border: 'none', boxShadow: '0 10px 15px rgba(0,0,0,0.1)' }} />
                                        <Legend verticalAlign="top" align="right" iconType="circle" />
                                        <Area name="Income" type="monotone" dataKey="income" stroke="#4318FF" strokeWidth={4} fill="url(#colorInc)" />
                                        <Area name="Costs" type="monotone" dataKey="costs" stroke="#EE5D50" strokeWidth={4} fill="url(#colorExp)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-5 p-3 h-100">
                        <CardBody>
                            <h5 className="fw-800 text-dark mb-5">Expense Allocation</h5>
                            <div style={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={expensePie} innerRadius={80} outerRadius={110} paddingAngle={8} dataKey="value">
                                            {expensePie.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} cornerRadius={10} />)}
                                        </Pie>
                                        <Tooltip />
                                        <Legend verticalAlign="bottom" iconType="circle" />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                .rounded-5 { border-radius: 25px !important; }
                .fw-800 { font-weight: 800; }
                .transition-up:hover { transform: translateY(-8px); transition: 0.4s; cursor: pointer; }
                .shadow-indigo { box-shadow: 0px 10px 20px rgba(67, 24, 255, 0.2); }
            `}</style>
        </div>
    );
};

export default AnalyticsMasterDashboard;